import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PreviewIcon from '@mui/icons-material/Preview';
import SearchIcon from '@mui/icons-material/Search';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import type { ArgoCdApi } from './api';
import type { ArgoAppSet, ArgoAppSetsResponse, ArgoGeneratedApp } from './types';
import { COLORS, StatusChip } from './ui';

export function AppSetsView({ api }: { api: ArgoCdApi }) {
  const [data, setData] = useState<ArgoAppSetsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [preview, setPreview] = useState<ArgoAppSet | null>(null);
  const [generated, setGenerated] = useState<ArgoGeneratedApp[] | null>(null);
  const [generateError, setGenerateError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await api.applicationSets());
    } catch (loadError) {
      setError(
        loadError instanceof Error ? loadError.message : String(loadError),
      );
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    load();
  }, [load]);

  const openPreview = async (appSet: ArgoAppSet) => {
    setPreview(appSet);
    setGenerated(null);
    setGenerateError(null);
    try {
      setGenerated(await api.appSetGenerate(appSet));
    } catch (previewError) {
      setGenerateError(
        previewError instanceof Error
          ? previewError.message
          : String(previewError),
      );
    }
  };

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    const appSets = data?.applicationSets ?? [];
    if (!query) return appSets;
    return appSets.filter(appSet =>
      [appSet.name, appSet.instance, appSet.project, appSet.repoUrl]
        .concat(appSet.generators)
        .some(value => value?.toLowerCase().includes(query)),
    );
  }, [data, search]);

  if (loading && !data) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {data?.instances.some(instance => !instance.connected) && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {data.instances
            .filter(instance => !instance.connected)
            .map(instance => `${instance.name}: ${instance.error}`)
            .join(' · ')}
        </Alert>
      )}
      <TextField
        size="small"
        value={search}
        onChange={event => setSearch(event.target.value)}
        placeholder="Search ApplicationSets..."
        sx={{ mb: 2, width: 340 }}
        InputProps={{
          startAdornment: <SearchIcon sx={{ mr: 1, color: COLORS.muted }} />,
        }}
      />
      <TableContainer
        sx={{ border: `1px solid ${COLORS.border}`, borderRadius: 2 }}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Instance</TableCell>
              <TableCell>Generators</TableCell>
              <TableCell>Source</TableCell>
              <TableCell align="right">Apps</TableCell>
              <TableCell>Status</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map(appSet => {
              const errorCondition = appSet.conditions.find(
                condition =>
                  condition.type === 'ErrorOccurred' &&
                  condition.status === 'True',
              );
              return (
                <TableRow hover key={`${appSet.instance}/${appSet.name}`}>
                  <TableCell>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 600 }}>
                      {appSet.name}
                    </Typography>
                    <Typography sx={{ color: COLORS.muted, fontSize: 10.5 }}>
                      {appSet.namespace ?? '—'} · {appSet.project ?? '—'}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ fontSize: 12 }}>{appSet.instance}</TableCell>
                  <TableCell>
                    {appSet.generators.map((generator, index) => (
                      <Chip
                        key={`${generator}-${index}`}
                        size="small"
                        label={generator}
                        sx={{ mr: 0.5, fontSize: 11 }}
                      />
                    ))}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 260 }}>
                    <Typography
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontFamily: 'monospace',
                        fontSize: 11,
                      }}
                      title={`${appSet.repoUrl ?? ''} ${appSet.path ?? ''}`}
                    >
                      {appSet.repoUrl ?? '—'}
                      {appSet.path ? ` · ${appSet.path}` : ''}
                    </Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: 12 }}>
                    {appSet.appCount}
                  </TableCell>
                  <TableCell>
                    {errorCondition ? (
                      <Tooltip title={errorCondition.message ?? ''}>
                        <Box component="span">
                          <StatusChip label="Error" color={COLORS.red} />
                        </Box>
                      </Tooltip>
                    ) : (
                      <StatusChip label="Healthy" color={COLORS.green} />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Preview generated applications">
                      <IconButton
                        size="small"
                        onClick={() => openPreview(appSet)}
                      >
                        <PreviewIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No ApplicationSets found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={Boolean(preview)}
        onClose={() => setPreview(null)}
        fullWidth
        maxWidth="md"
      >
        {preview && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ flex: 1 }}>
                  Generated applications — {preview.name}
                  <Typography sx={{ color: COLORS.muted, fontSize: 12 }}>
                    {preview.instance} · {preview.generators.join(', ')}
                  </Typography>
                </Box>
                <IconButton onClick={() => setPreview(null)}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              {generateError && <Alert severity="error">{generateError}</Alert>}
              {!generated && !generateError && (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress size={26} />
                </Box>
              )}
              {generated && (
                <TableContainer
                  sx={{ border: `1px solid ${COLORS.border}`, borderRadius: 1 }}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Application</TableCell>
                        <TableCell>Destination</TableCell>
                        <TableCell>Source</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {generated.map((application, index) => (
                        <TableRow key={`${application.name}-${index}`}>
                          <TableCell sx={{ fontSize: 12 }}>
                            <strong>{application.name}</strong>
                            <Typography
                              sx={{ color: COLORS.muted, fontSize: 10.5 }}
                            >
                              {application.project}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ fontSize: 12 }}>
                            {application.destinationName ??
                              application.destinationServer ??
                              '—'}
                            <Typography
                              sx={{ color: COLORS.muted, fontSize: 10.5 }}
                            >
                              {application.destinationNamespace ?? '—'}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ maxWidth: 300 }}>
                            <Typography
                              sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                fontFamily: 'monospace',
                                fontSize: 11,
                              }}
                              title={`${application.repoUrl ?? ''} ${
                                application.path ?? ''
                              } @ ${application.targetRevision ?? ''}`}
                            >
                              {application.path ?? application.repoUrl ?? '—'} @{' '}
                              {application.targetRevision ?? 'HEAD'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                      {generated.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={3} align="center">
                            No applications generated
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </DialogContent>
          </>
        )}
      </Dialog>
    </Box>
  );
}
